import useSWR from 'swr';
import { MdEdit } from 'react-icons/md';
import { User } from 'views/types/typeUser';
import { usersService } from 'views/services/usersService';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { formatNumber } from 'utils/formatNumber';
import { EditBalanceDialog } from '../EditBalanceDialog';

interface IProps {
  user: User;
  onChanged: () => void;
}

const thCls =
  'px-3 py-2 text-start text-xs font-semibold uppercase text-gray-500 dark:text-white/60';
const tdCls = 'px-3 py-2 align-top';

export const UserBalances: React.FC<IProps> = ({ user, onChanged }) => {
  const { canUpdate } = usePermissions();

  const { data: balances, mutate } = useSWR(
    `user-balances-${user.id}`,
    () => usersService.getUserBalances(user.id),
    { refreshInterval: 10000 }
  );

  const canEdit =
    canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) &&
    canUpdate(PERMISSION_RESOURCES.CLIENT_PROFILE) &&
    canUpdate(PERMISSION_RESOURCES.CLIENT_BALANCE_EDIT);

  if (!balances) {
    return <div className="font-semibold ml-3">Loading...</div>;
  }

  return (
    <div className="ml-3 overflow-x-auto">
      <table className="min-w-full border border-gray-300 dark:border-gray-600">
        <thead>
          <tr className="border-b border-gray-300 dark:border-gray-600">
            <th className={thCls}>Currency</th>
            <th className={thCls}>Total Balance</th>
            <th className={thCls}>Available Balance</th>
            <th className={thCls}>Total Blocked Balance</th>
          </tr>
        </thead>
        <tbody>
          {balances.map(item => {
            const amountInUsdt = user.balances?.[item.currency]?.amountInUsdt;
            const handleSaved = () => {
              mutate();
              onChanged();
            };

            return (
              <tr
                key={item.currency}
                className="border-b border-gray-200 font-semibold last:border-b-0 dark:border-gray-700"
              >
                <td className={tdCls}>
                  {item.currency}
                  {amountInUsdt != null && amountInUsdt !== 0 && (
                    <div className="text-[12px] font-normal dark:text-white/50 text-[black]/50">
                      ≈ {formatNumber(amountInUsdt)} USDT
                    </div>
                  )}
                </td>
                <td className={tdCls}>{item.totalBalance ?? item.balance}</td>
                <td className={tdCls}>
                  <span className="inline-flex items-center gap-1">
                    {item.balance}
                    {canEdit && (
                      <EditBalanceDialog
                        userId={user.id}
                        item={item}
                        balanceType="available"
                        onSaved={handleSaved}
                      >
                        <button
                          type="button"
                          aria-label={`Edit ${item.currency} available balance`}
                          className="inline-flex h-[22px] w-[22px] items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                        >
                          <MdEdit className="h-4 w-4" />
                        </button>
                      </EditBalanceDialog>
                    )}
                  </span>
                </td>
                <td className={tdCls}>
                  <div>{item.totalBlocked ?? item.blockedBalance}</div>
                  <div className="mt-1 flex flex-col gap-0.5 text-[12px] font-normal dark:text-white/60 text-[black]/60">
                    <span>Deposit AML: {item.blockedDepositAml ?? '0'}</span>
                    <span>Withdraw: {item.blockedWithdraw ?? '0'}</span>
                    <span className="inline-flex items-center gap-1">
                      Admin: {item.blockedBalance}
                      {canEdit && (
                        <EditBalanceDialog
                          userId={user.id}
                          item={item}
                          balanceType="blocked"
                          onSaved={handleSaved}
                        >
                          <button
                            type="button"
                            aria-label={`Edit ${item.currency} admin blocked balance`}
                            className="inline-flex h-[20px] w-[20px] items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                          >
                            <MdEdit className="h-3.5 w-3.5" />
                          </button>
                        </EditBalanceDialog>
                      )}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
